import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import { fetchTerminal, fetchCategories } from "../lib/api";
import TerminalView from "../components/TerminalView";

// Remember the most recent terminal so the standalone PWA launcher (/app)
// can open straight into it.
export const LAST_TERMINAL_KEY = "tv_terminal_id";

/**
 * Personal Terminal — a shareable, brutalist "command deck" of the tools the
 * AI concierge recommended (built via the secret word in the chat). Tools are
 * grouped by category in a two-column deck with a persistent menu bar.
 */
export default function Terminal() {
  const { id } = useParams();
  const [term, setTerm] = useState(null);
  const [status, setStatus] = useState("loading");
  const [catOrder, setCatOrder] = useState([]);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    fetchTerminal(id)
      .then((t) => {
        if (!alive) return;
        setTerm(t);
        setStatus("ok");
        try {
          localStorage.setItem(LAST_TERMINAL_KEY, id);
        } catch {
          /* storage blocked — non-fatal */
        }
      })
      .catch((err) => {
        if (!alive) return;
        setStatus(err?.response?.status === 404 ? "notfound" : "error");
      });
    fetchCategories()
      .then((d) => alive && setCatOrder(d.categories || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id]);

  if (status === "loading") {
    return (
      <div data-testid="terminal-loading">
        <Seo title="Loading terminal" path={`/terminal/${id}`} noindex />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <div className="text-[13px] text-[#888]">
            booting terminal...<span className="tv-blink">_</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div data-testid="terminal-404">
        <Seo title="Terminal not found" path={`/terminal/${id}`} noindex />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold border-b border-black pb-2">
            {">"}_ 404 / no such terminal
          </h1>
          <p className="mt-4 text-[13px] text-[#444]">
            That terminal ID doesn&apos;t exist. It may have been mistyped.
          </p>
          <p className="mt-4">
            <Link to="/ask" className="tv-btn">
              [→ build a new one]
            </Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "error" || !term) {
    return (
      <div data-testid="terminal-error">
        <Seo title="Error" path={`/terminal/${id}`} noindex />
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold">{">"}_ error</h1>
          <p className="text-[13px] text-[#444] mt-2">could not load terminal. try again.</p>
        </main>
        <Footer />
      </div>
    );
  }

  const title = term.title || "my terminal";
  const count = term.tools.length;
  const sample = term.tools
    .slice(0, 4)
    .map((t) => t.title)
    .join(", ");
  const seoDesc = `${count} investing tools — ${sample}${
    count > 4 ? `, +${count - 4} more` : ""
  }. A personal terminal built on TerminalValue.io.`;

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Terminal: ${title}`,
    numberOfItems: count,
    itemListElement: term.tools.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: t.slug ? siteUrl(`/tool/${t.slug}`) : t.url,
      name: t.title,
    })),
  };

  return (
    <div data-testid="terminal-page">
      <Seo
        title={`Terminal: ${title}`}
        description={seoDesc}
        path={`/terminal/${id}`}
        ogType="article"
        noindex
        jsonLd={itemListLd}
      />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <TerminalView id={id} term={term} setTerm={setTerm} catOrder={catOrder} />
        <div className="mt-10 text-[12px] text-[#666] border-t border-[#eee] pt-4">
          <p>
            this terminal is a snapshot built from the concierge&apos;s
            recommendations. tools change; the snapshot doesn&apos;t.
          </p>
          <p className="mt-2">
            <Link to="/ask" className="tv-link">
              [→ ask the concierge for more]
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
